<template>
    <div ref="containerMessageDisplay" :style="{background: colors.message.messagesDisplay.bg}" class="container-message-display"
         @scroll="updateScrollState">
        <div v-if="loading" class="loader">
            <div class="message-loading"></div>
        </div>
        <li v-for="(message, index) in messages" :key="index" class="message-li">
            <template v-if="displayTime">
                <p class="time">
                    <span>{{message.timestamp.format('LT')}}</span>
                </p>
            </template>
            <template v-if="displayUsername">
                <p v-if="!message.myself" class="message-username">{{getParticipantById(message.participantId).name}}</p>
                <p v-else class="message-username" :class="{'myself-username': message.myself}">{{myself.name}}</p>
            </template>
            <div class="main" :class="{ self: message.myself }">
                <el-avatar class="avatar" :size="avatar.size" :shape="avatar.shape" :src="message.myself ? myself.avatar : getParticipantById(message.participantId).avatar "/>
                <div class="text" :style="{background: !message.myself ? colors.message.others.bg : colors.message.myself.bg, color: !message.myself ? colors.message.others.text : colors.message.myself.text}">
                    {{message.content}}
                </div>
            </div>
        </li>
    </div>
</template>

<script>
	import {
		Avatar
	} from 'element-ui';
	import 'element-ui/lib/theme-chalk/index.css';
	import {
		mapGetters,
		mapMutations
	} from 'vuex';

	export default {
		components: {
			'el-avatar': Avatar
		},
		props: {
			avatar: {
				type: Object,
				required: false,
				default: () => {
					return {
						size: 'medium',
						shape: 'square'
					}
				}
			},
			colors: {
				type: Object,
				required: true
			},
			asyncMode: {
				type: Boolean,
				required: false,
				default: false
			},
			loadMoreMessages: {
				type: Function,
				required: false,
				default: null
			},
			scrollBottom: {
				type: Object,
				required: true
			},
			displayUsername: {
				type: Boolean,
				required: false,
				default: true
			},
			displayTime: {
				type: Boolean,
				required: false,
				default: true
			}
		},
		data() {
			return {
				updateScroll: true,
				lastMessage: null,
				loading: false
			}
		},
		computed: {
			...mapGetters([
				'getParticipantById',
				'messages',
				'myself'
			]),
		},
		mounted() {
			this.goToBottom();
			this.$refs.containerMessageDisplay.dispatchEvent(new CustomEvent('scroll'));
		},
		updated() {
			if (this.messages.length && !this.messageCompare(this.messages[this.messages.length - 1], this.lastMessage)) {

				if (this.updateScroll || (this.scrollBottom.messageSent && this.messages[this.messages.length - 1].participantId ==
						this.myself.id) || (this.scrollBottom.messageReceived && this.messages[this.messages.length - 1].participantId !=
						this.myself.id)) {
					this.goToBottom();
					if (this.messages.length) {
						this.lastMessage = this.messages[this.messages.length - 1]
					}
				}
			}
		},
		methods: {
			...mapMutations([
				'setMessages',
			]),
			/**
			 * This function compare two messages without looking at the uploaded propertie.
			 * This function has been implemented to prevent chat scrolling down after changing the message from 'uploaded = false' to 'uploaded = true'.
			 * @param {Object} message1 the first message object 
			 * @param {Object} message2 the second message object
			 * @return {Boolean} true if the messages are equal and false if they are different
			 */
			messageCompare(message1, message2) {
				/**
				 * if one of the messages are null, you can safely compare the messages with '==='
				 */
				if (!message2 || !message1) {
					return message1 === message2
				}
				/**
				 * compare the immutable properties of a message
				 */
				let participant_equal = message1.participantId == message2.participantId;
				let content_equal = message1.content == message2.content;
				let timestamp_equal = message1.timestamp.isSame(message2.timestamp);

				return participant_equal && content_equal && timestamp_equal
			},
			updateScrollState({
				target: {
					scrollTop,
					clientHeight,
					scrollHeight
				}
			}) {
				this.updateScroll = scrollTop + clientHeight >= scrollHeight;

				if (typeof this.loadMoreMessages === 'function' && scrollTop < 20) {
					this.loading = true;
					this.loadMoreMessages((messages) => {
						if (Array.isArray(messages) && messages.length > 0) {
							this.setMessages([...messages, ...this.messages]);
						}
						this.loading = false;
					});
				}
			},
			goToBottom() {
				let scrollDiv = this.$refs.containerMessageDisplay;
				scrollDiv.scrollTop = scrollDiv.scrollHeight;

				this.updateScroll = false;
			}
		}
	}
</script>

<style lang="less">
	.quick-chat-container .container-message-display {
		flex: 1;
		overflow-y: scroll;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		padding-bottom: 10px;
		max-height: 100%;

		/************** Safari 10.1+ ********************/
		@media not all and (min-resolution: .001dpcm) {
			@supports (-webkit-appearance: none) {

				.message-container {
					display: -webkit-box !important;
				}

			}
		}

		.message-text {
			background: #fff;
			padding: 6px 10px;
			line-height: 14px;
			border-radius: 15px;
			margin: 5px 0 5px 0;
			max-width: 70%;
			overflow-wrap: break-word;
			text-align: left;
			white-space: pre-wrap;
		}

		.message-text>p {
			margin: 5px 0 5px 0;
			font-size: 14px;
		}

		.message-timestamp {
			padding: 2px 7px;
			border-radius: 15px;
			margin: 0;
			max-width: 50%;
			overflow-wrap: break-word;
			text-align: left;
			font-size: 10px;
			color: #bdb8b8;
			width: 100%;
			display: flex;
			align-items: center;
		}

		.my-message>.message-timestamp {
			text-align: right;
		}

		.my-message {
			justify-content: flex-end;
			padding-right: 15px;
			align-items: flex-end;
		}

		.other-message {
			justify-content: flex-start;
			padding-left: 15px;
			align-items: flex-start;
		}

		.other-message>.message-text {
			color: #fff;
			border-bottom-left-radius: 0;
		}

		.my-message>.message-text {
			border-bottom-right-radius: 0;
		}

		.message-container {
			display: flex;
			flex-wrap: wrap;
			flex-direction: column;
		}

		.message-username {
			font-size: 12px;
			font-weight: bold;
			text-align: left;
			padding: 0 50px;
		}

		.myself-username {
			text-align: right;
		}

		.icon-sent {
			width: 12px;
			padding-left: 5px;
			color: rgb(129, 127, 127);
		}

		.message-loading {
			height: 8px;
			width: 8px;
			border: 1px solid rgb(187, 183, 183);
			border-left-color: rgb(59, 59, 59);
			border-radius: 50%;
			margin-left: 5px;
			display: inline-block;
			animation: spin 1.3s ease infinite;
		}

		.loader .message-loading {
			width: 16px;
			height: 16px;
			margin: 5px 0 0 0;
		}

		.message-li {
			margin-bottom: 15px;
			list-style-type: none;
		}

		.time {
			margin: 7px 0;
			text-align: center;

			>span {
				display: inline-block;
				padding: 0 18px;
				font-size: 12px;
				color: #fff;
				border-radius: 2px;
				background-color: #dcdcdc;
			}
		}

		.avatar {
			float: left;
			margin: 0 5px 0 5px;
		}

		.text {
			display: inline-block;
			position: relative;
			padding: 0 10px;
			max-width: ~'calc(100% - 70px)';
			min-height: 30px;
			line-height: 2.5;
			font-size: 14px;
			text-align: left;
			word-break: break-all;
			background-color: #fafafa;
			border-radius: 4px;
			text-align: left;

			&:before {
				content: " ";
				position: absolute;
				top: 9px;
				right: 100%;
				border: 6px solid transparent;
				border-right-color: #fafafa;
			}
		}

		.main {
			text-align: left;
		}

		.self {
			text-align: right;

			.avatar {
				float: right;
				margin: 0 5px 0 5px;
			}

			.text {
				background-color: #b2e281;

				&:before {
					right: inherit;
					left: 100%;
					border-right-color: transparent;
					border-left-color: #b2e281;
				}
			}
		}
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}
</style>
